import React, { useState, useMemo, useRef } from 'react';
import {
  Plus, Edit2, Trash2, X, ChevronDown, ChevronUp, Phone, Mail, MessageCircle,
  Calendar, Clock, AlertCircle, FileText, Paperclip, Download, User, Target,
  CheckCircle, Link as LinkIcon,
} from 'lucide-react';
import { toast } from '../utils/toast';
import { useNamespaceEmpresa } from '../hooks/useNamespaceEmpresa';

const EMPTY_ARR = Object.freeze([]);

// ─── Constantes ───────────────────────────────────────────────────────────────
export const ESTAGIOS = [
  { id: 'NOVO',         label: 'Novo',           cor: '#5f5874' },
  { id: 'QUALIFICANDO', label: 'Qualificando',   cor: '#3b82f6' },
  { id: 'REUNIAO',      label: 'Reunião',        cor: '#06b6d4' },
  { id: 'PROPOSTA',     label: 'Proposta',       cor: '#f59e0b' },
  { id: 'NEGOCIACAO',   label: 'Negociação',     cor: '#ec4899' },
  { id: 'GANHO',        label: 'Ganho',          cor: '#10b981' },
  { id: 'PERDIDO',      label: 'Perdido',        cor: '#ef4444' },
];

export const ORIGENS = ['Indicação', 'Site', 'LinkedIn', 'Evento', 'WhatsApp', 'Outro'];

export const CANAIS = [
  { id: 'EMAIL',     label: 'E-mail',     emoji: '✉️', icon: Mail },
  { id: 'WHATSAPP',  label: 'WhatsApp',   emoji: '💬', icon: MessageCircle },
  { id: 'LIGACAO',   label: 'Ligação',    emoji: '📞', icon: Phone },
  { id: 'REUNIAO',   label: 'Reunião',    emoji: '🤝', icon: Calendar },
  { id: 'VISITA',    label: 'Visita',     emoji: '🚗', icon: User },
  { id: 'OUTRO',     label: 'Outro',      emoji: '📝', icon: FileText },
];

const FORM_VAZIO = () => ({
  id: null,
  cliente: '',
  empresa: '',
  origem: 'Indicação',
  estagio: 'NOVO',
  responsavel: '',
  valorEstimado: '',
  probabilidade: '',
  contatos: [],
  interacoes: [],
  proximoContato: '',
  proximoResponsavel: '',
  proximoObs: '',
  propostasIds: [],
  orcamentosIds: [],
  observacoes: '',
  criadoEm: new Date().toISOString(),
});

const CONTATO_VAZIO = () => ({ id: Date.now() + Math.random(), nome: '', cargo: '', email: '', whatsapp: '' });

const INTERACAO_VAZIA = (autor) => ({
  id: Date.now() + Math.random(),
  data: new Date().toISOString().slice(0, 16),
  canal: 'EMAIL',
  autor: autor || '',
  destinatarios: '',
  observacao: '',
  anexos: [],
});

// Converte arquivo em { nome, tipo, tamanho, dados (base64) }
const arquivoParaBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload  = () => resolve({ nome: file.name, tipo: file.type, tamanho: file.size, dados: reader.result });
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Status de follow-up baseado na data prevista (hoje, atrasado, futuro)
export const statusFollowup = (proximoContato) => {
  if (!proximoContato) return null;
  const hoje  = new Date(); hoje.setHours(0, 0, 0, 0);
  const data  = new Date(proximoContato + 'T00:00');
  const diff  = Math.round((data - hoje) / 86400000);
  if (diff < 0)  return { tipo: 'atrasado', diasRestantes: diff, label: `Atrasado ${Math.abs(diff)}d` };
  if (diff === 0) return { tipo: 'hoje', diasRestantes: 0, label: 'Hoje' };
  if (diff <= 7) return { tipo: 'breve', diasRestantes: diff, label: `Em ${diff}d` };
  return { tipo: 'futuro', diasRestantes: diff, label: `Em ${diff}d` };
};

const COR_FOLLOWUP = { atrasado: '#ef4444', hoje: '#f59e0b', breve: '#3b82f6', futuro: '#5f5874' };

// ─── Componente principal ─────────────────────────────────────────────────────
export function Prospeccao({
  oportunidades, setOportunidades,
  clientes,
  usuarios,
  profissionalAtual, isAdmin,
  propostas: propostasLegado, setPropostas: setPropostasLegado,
  propostasPorEmpresa, setPropostasPorEmpresa,
  orcamentosKzsPorEmpresa,
}) {
  // Propostas namespaced por empresa: Kzero Consultoria herda lazy o legado;
  // demais empresas começam vazias. Setter wrapper escreve em mapa[slug].
  const [propostas, setPropostas] = useNamespaceEmpresa({
    mapaPorEmpresa: propostasPorEmpresa, setMapaPorEmpresa: setPropostasPorEmpresa,
    legado: propostasLegado, setLegado: setPropostasLegado, vazio: EMPTY_ARR,
  });
  // Orçamentos KzeroSystem (Financeiro) também se vinculam à oportunidade
  // (30/10/2026). Só leitura aqui: o vínculo fica em oportunidade.orcamentosIds.
  const [orcamentosKzs] = useNamespaceEmpresa({
    mapaPorEmpresa: orcamentosKzsPorEmpresa, legado: null, vazio: EMPTY_ARR,
  });

  const [form, setForm]         = useState(null);
  const [expandido, setExpandido] = useState({});
  const [filtroEstagio, setFiltroEstagio] = useState('TODOS');
  const [filtroResponsavel, setFiltroResponsavel] = useState('TODOS');
  const [visao, setVisao] = useState('lista'); // 'lista' | 'kanban' | 'followups'
  const [novoContato, setNovoContato] = useState(null);
  const [novaInteracao, setNovaInteracao] = useState(null);
  const [vincularProposta, setVincularProposta] = useState(false);
  const fileInputRef = useRef(null);

  const opps = oportunidades || [];

  // Lista de usuários para seletor de responsável.
  // Apenas usuários cujo cadastro tem a empresa "Kzero" (comparação case-insensitive).
  const opcoesResponsavel = useMemo(() => {
    const ehKzero = (empresa) => !!empresa && String(empresa).toLowerCase().includes('kzero');
    return (usuarios || [])
      .filter(u => u.nome && ehKzero(u.empresa))
      .map(u => ({ valor: u.nome, label: u.nome, email: u.email }));
  }, [usuarios]);

  const emailDoResponsavel = (nome) => {
    const u = (usuarios || []).find(x => x.nome === nome);
    return u?.email || '';
  };

  // Filtragem
  const oppsVisiveis = useMemo(() => {
    let lista = opps;
    if (!isAdmin) lista = lista.filter(o => !o.responsavel || o.responsavel === profissionalAtual);
    if (filtroEstagio !== 'TODOS') lista = lista.filter(o => o.estagio === filtroEstagio);
    if (filtroResponsavel !== 'TODOS') lista = lista.filter(o => o.responsavel === filtroResponsavel);
    // Mais recentes no topo (30/10/2026). criadoEm; sem ele, o id (Date.now).
    const quando = o => Date.parse(o.criadoEm || '') || Number(o.id) || 0;
    return [...lista].sort((a, b) => quando(b) - quando(a));
  }, [opps, isAdmin, profissionalAtual, filtroEstagio, filtroResponsavel]);

  // Follow-ups pendentes (atrasados + hoje + próximos 7 dias)
  const followupsPendentes = useMemo(() => {
    return oppsVisiveis
      .filter(o => o.proximoContato && !['GANHO', 'PERDIDO'].includes(o.estagio))
      .map(o => ({ ...o, _status: statusFollowup(o.proximoContato) }))
      .filter(o => o._status && o._status.diasRestantes <= 7)
      .sort((a, b) => a._status.diasRestantes - b._status.diasRestantes);
  }, [oppsVisiveis]);

  // ─── Ações de oportunidade ──────────────────────────────────────────────────
  const novo  = () => setForm({ ...FORM_VAZIO(), responsavel: profissionalAtual || '' });
  const editar = (o) => setForm({ ...FORM_VAZIO(), ...o });
  const fechar = () => { setForm(null); setNovoContato(null); setNovaInteracao(null); setVincularProposta(false); };

  const salvar = () => {
    if (!form.cliente && !form.empresa) { toast.aviso('Informe pelo menos cliente ou empresa.'); return; }
    const editando = form.id && opps.find(o => o.id === form.id);
    const payload = editando ? form : { ...form, id: Date.now() };
    setOportunidades(editando ? opps.map(o => o.id === form.id ? payload : o) : [...opps, payload]);
    toast.sucesso(editando ? 'Oportunidade atualizada!' : 'Oportunidade criada!');
    fechar();
  };

  const excluir = (id) => {
    if (!window.confirm('Excluir esta oportunidade? O histórico de interações será perdido.')) return;
    setOportunidades(opps.filter(o => o.id !== id));
    toast.sucesso('Oportunidade excluída.');
  };

  const moverEstagio = (id, novoEstagio) => {
    setOportunidades(opps.map(o => o.id === id ? { ...o, estagio: novoEstagio } : o));
  };

  // ─── Contatos ───────────────────────────────────────────────────────────────
  const adicionarContato = () => {
    if (!novoContato?.nome?.trim()) { toast.aviso('Informe o nome do contato.'); return; }
    setForm(f => ({ ...f, contatos: [...(f.contatos || []), { ...novoContato, id: Date.now() }] }));
    setNovoContato(null);
  };
  const removerContato = (id) => setForm(f => ({ ...f, contatos: (f.contatos || []).filter(c => c.id !== id) }));
  const atualizarContato = (id, campo, valor) =>
    setForm(f => ({ ...f, contatos: (f.contatos || []).map(c => c.id === id ? { ...c, [campo]: valor } : c) }));

  // ─── Interações ─────────────────────────────────────────────────────────────
  const iniciarInteracao = () => setNovaInteracao(INTERACAO_VAZIA(profissionalAtual));

  const adicionarInteracao = () => {
    if (!novaInteracao?.observacao?.trim()) { toast.aviso('Descreva a interação.'); return; }
    setForm(f => ({
      ...f,
      interacoes: [...(f.interacoes || []), { ...novaInteracao, id: Date.now() }]
        .sort((a, b) => (b.data || '').localeCompare(a.data || '')),
    }));
    setNovaInteracao(null);
  };

  const removerInteracao = (id) =>
    setForm(f => ({ ...f, interacoes: (f.interacoes || []).filter(i => i.id !== id) }));

  const anexarArquivo = async (arquivos) => {
    const lista = Array.from(arquivos || []);
    if (!lista.length) return;
    try {
      const anexos = await Promise.all(lista.map(arquivoParaBase64));
      setNovaInteracao(i => ({ ...i, anexos: [...(i.anexos || []), ...anexos] }));
    } catch (e) {
      toast.erro('Erro ao anexar arquivo.');
    }
  };

  const baixarAnexo = (anexo) => {
    const link = document.createElement('a');
    link.href = anexo.dados;
    link.download = anexo.nome;
    link.click();
  };

  // ─── Vincular proposta existente ────────────────────────────────────────────
  const propostasDisponiveis = useMemo(() => {
    if (!form) return [];
    const ids = new Set(form.propostasIds || []);
    return (propostas || []).filter(p => !ids.has(p.id));
  }, [propostas, form]);

  const vincularPropostaId = (id) => {
    setForm(f => ({ ...f, propostasIds: [...(f.propostasIds || []), id] }));
    // espelha vínculo na proposta — facilita relatórios
    if (setPropostas) {
      setPropostas((propostas || []).map(p => p.id === id ? { ...p, oportunidadeId: form.id } : p));
    }
    setVincularProposta(false);
  };

  const desvincularProposta = (id) => {
    setForm(f => ({ ...f, propostasIds: (f.propostasIds || []).filter(p => p !== id) }));
    if (setPropostas) {
      setPropostas((propostas || []).map(p => p.id === id ? { ...p, oportunidadeId: null } : p));
    }
  };

  const propostasVinculadas = useMemo(() => {
    if (!form) return [];
    return (propostas || []).filter(p => (form.propostasIds || []).includes(p.id));
  }, [propostas, form]);

  const orcamentosVinculados = useMemo(() => {
    if (!form) return [];
    const ids = new Set((form.orcamentosIds || []).map(String));
    return (orcamentosKzs || []).filter(o => ids.has(String(o.id)));
  }, [orcamentosKzs, form]);

  const vincularOrcamentoId = (id) => {
    setForm(f => ({ ...f, orcamentosIds: [...(f.orcamentosIds || []), id] }));
    setVincularProposta(false);
  };
  const desvincularOrcamento = (id) => {
    setForm(f => ({ ...f, orcamentosIds: (f.orcamentosIds || []).filter(x => String(x) !== String(id)) }));
  };

  // Propostas e orçamentos numa lista só, em ordem alfabética de cliente.
  const vinculaveisDisponiveis = useMemo(() => {
    if (!form) return [];
    const idsOrc = new Set((form.orcamentosIds || []).map(String));
    const itens = [
      ...propostasDisponiveis.map(p => ({
        chave: `p-${p.id}`, cliente: p.cliente || '', vincular: () => vincularPropostaId(p.id),
        texto: `Proposta — ${p.cliente} — ${p.data} — ${p.status}`,
      })),
      ...(orcamentosKzs || []).filter(o => !idsOrc.has(String(o.id))).map(o => ({
        chave: `o-${o.id}`, cliente: o.cliente || '', vincular: () => vincularOrcamentoId(o.id),
        texto: `Orçamento ${o.numero || ''} — ${o.cliente} — ${o.data || ''} — ${o.status || ''}`,
      })),
    ];
    return itens.sort((a, b) => a.cliente.trim().localeCompare(b.cliente.trim(), 'pt-BR'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propostasDisponiveis, orcamentosKzs, form]);

  // ─── Render: cards e lista ──────────────────────────────────────────────────
  const renderCard = (o) => {
    const est = ESTAGIOS.find(e => e.id === o.estagio) || ESTAGIOS[0];
    const exp = expandido[o.id];
    const fu = statusFollowup(o.proximoContato);
    const corFu = fu ? COR_FOLLOWUP[fu.tipo] : null;
    const ultima = (o.interacoes || []).slice().sort((a,b)=>(b.data||'').localeCompare(a.data||''))[0];
    return (
      <div key={o.id} style={s.oppCard}>
        <div style={s.oppHeader} onClick={() => setExpandido(p => ({ ...p, [o.id]: !p[o.id] }))}>
          <div style={s.oppInfo}>
            <div style={s.oppNome}>{o.cliente || o.empresa || 'Sem nome'}</div>
            <div style={s.oppMeta}>
              {o.empresa && o.cliente && <span>{o.empresa} · </span>}
              {o.responsavel && <span>👤 {o.responsavel} · </span>}
              {o.valorEstimado && <span>R$ {parseFloat(o.valorEstimado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>}
            </div>
            {fu && (
              <div style={{ ...s.oppFu, color: corFu, borderColor: corFu }}>
                <Clock size={12} /> {fu.label} {o.proximoResponsavel && `· ${o.proximoResponsavel}`}
              </div>
            )}
          </div>
          <div style={s.oppAcoes}>
            <span style={{ ...s.estagioBadge, color: est.cor, borderColor: est.cor }}>{est.label}</span>
            <button style={s.btnAcao} onClick={e => { e.stopPropagation(); editar(o); }} title="Editar"><Edit2 size={15} /></button>
            <button style={s.btnAcaoDel} onClick={e => { e.stopPropagation(); excluir(o.id); }} title="Excluir"><Trash2 size={15} /></button>
            {exp ? <ChevronUp size={16} color="#5f5874" /> : <ChevronDown size={16} color="#5f5874" />}
          </div>
        </div>
        {exp && (
          <div style={s.oppDetalhes}>
            {o.origem && <div style={s.detLinha}><span style={s.detLabel}>Origem:</span> {o.origem}</div>}
            {(o.contatos || []).length > 0 && (
              <div>
                <div style={s.detLabel}>Contatos</div>
                {(o.contatos || []).map(c => (
                  <div key={c.id} style={s.contatoChip}>
                    <strong>{c.nome}</strong>{c.cargo && ` — ${c.cargo}`}
                    {c.email && <a href={`mailto:${c.email}`} style={s.linkContato}><Mail size={11}/> {c.email}</a>}
                    {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={s.linkContato}><MessageCircle size={11}/> {c.whatsapp}</a>}
                  </div>
                ))}
              </div>
            )}
            {ultima && (
              <div>
                <div style={s.detLabel}>Última interação</div>
                <div style={s.interacaoItem}>
                  <span style={s.canalBadge}>{CANAIS.find(c=>c.id===ultima.canal)?.emoji} {CANAIS.find(c=>c.id===ultima.canal)?.label}</span>
                  <span style={{ fontSize: 12, color: '#5f5874' }}>{ultima.data?.replace('T',' ')}</span>
                  {ultima.autor && <span style={{ fontSize: 12, color: '#8b5cf6' }}>· {ultima.autor}</span>}
                  <div style={{ width:'100%', fontSize: 13, color: '#2d2640', marginTop: 4 }}>{ultima.observacao}</div>
                </div>
              </div>
            )}
            {(o.interacoes || []).length > 1 && (
              <div style={{ fontSize: 12, color: '#5f5874' }}>+ {(o.interacoes || []).length - 1} interação(ões) anterior(es) — abra para editar e ver tudo</div>
            )}
            {((o.propostasIds || []).length > 0 || (o.orcamentosIds || []).length > 0) && (
              <div>
                <div style={s.detLabel}>Propostas vinculadas</div>
                {(o.propostasIds || []).map(pid => {
                  const p = (propostas || []).find(x => x.id === pid);
                  if (!p) return null;
                  return <div key={pid} style={s.propostaChip}><FileText size={12}/> {p.cliente} — {p.data}</div>;
                })}
                {(o.orcamentosIds || []).map(oid => {
                  const orc = (orcamentosKzs || []).find(x => String(x.id) === String(oid));
                  if (!orc) return null;
                  return <div key={`o-${oid}`} style={s.propostaChip}><FileText size={12}/> Orçamento {orc.numero} — {orc.cliente}</div>;
                })}
              </div>
            )}
            {o.observacoes && <div style={{ fontSize: 13, color: '#2d2640', whiteSpace: 'pre-line' }}>{o.observacoes}</div>}
          </div>
        )}
      </div>
    );
  };

  // ─── Kanban ─────────────────────────────────────────────────────────────────
  const renderKanban = () => (
    <div style={s.kanban}>
      {ESTAGIOS.map(est => {
        const lista = oppsVisiveis.filter(o => o.estagio === est.id);
        return (
          <div key={est.id} style={{ ...s.kanbanCol, borderTopColor: est.cor }}>
            <div style={{ ...s.kanbanColHeader, color: est.cor }}>
              <span>{est.label}</span>
              <span style={s.kanbanCount}>{lista.length}</span>
            </div>
            <div style={s.kanbanBody}>
              {lista.map(o => {
                const fu = statusFollowup(o.proximoContato);
                const corFu = fu ? COR_FOLLOWUP[fu.tipo] : null;
                return (
                  <div key={o.id} style={s.kanbanCard} onClick={() => editar(o)}>
                    <div style={s.kanbanCardNome}>{o.cliente || o.empresa}</div>
                    {o.responsavel && <div style={s.kanbanCardMeta}>👤 {o.responsavel}</div>}
                    {o.valorEstimado && <div style={s.kanbanCardMeta}>R$ {parseFloat(o.valorEstimado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>}
                    {fu && <div style={{ ...s.kanbanCardFu, color: corFu, borderColor: corFu }}><Clock size={10} /> {fu.label}</div>}
                    <div style={s.kanbanCardActions} onClick={e => e.stopPropagation()}>
                      <select value={o.estagio} onChange={e => moverEstagio(o.id, e.target.value)} style={s.kanbanSelect}>
                        {ESTAGIOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}
              {lista.length === 0 && <div style={s.kanbanVazio}>—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─── Follow-ups ─────────────────────────────────────────────────────────────
  const renderFollowups = () => (
    <div style={s.fuList}>
      {followupsPendentes.length === 0 && (
        <div style={s.vazio}>
          <CheckCircle size={36} color="#10b981" />
          <p>Nenhum follow-up pendente para os próximos 7 dias.</p>
        </div>
      )}
      {followupsPendentes.map(o => {
        const cor = COR_FOLLOWUP[o._status.tipo];
        const ult = (o.interacoes || []).slice().sort((a,b)=>(b.data||'').localeCompare(a.data||''))[0];
        return (
          <div key={o.id} style={{ ...s.fuCard, borderLeftColor: cor }}>
            <div style={s.fuHeader}>
              <div>
                <div style={s.fuNome}>{o.cliente || o.empresa}</div>
                <div style={{ ...s.fuStatus, color: cor, borderColor: cor }}>
                  <Clock size={12} /> {o._status.label}
                </div>
              </div>
              <div style={s.fuRespBox}>
                <div style={s.detLabel}>Responsável</div>
                <div style={s.fuResp}>{o.proximoResponsavel || o.responsavel || '—'}</div>
              </div>
            </div>
            {o.proximoObs && <div style={s.fuObs}>{o.proximoObs}</div>}
            {ult && <div style={{ fontSize: 12, color: '#5f5874' }}>Última: {ult.data?.replace('T',' ')} ({CANAIS.find(c=>c.id===ult.canal)?.label})</div>}
            <div style={s.fuActions}>
              <button style={s.btnSec} onClick={() => editar(o)}><Edit2 size={13}/> Abrir</button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─── Render principal ───────────────────────────────────────────────────────
  return (
    <div className="m-container" style={s.container}>
      <div style={s.header}>
        <h2 style={s.titulo}>Prospecção</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {!form && <button style={s.btnNovo} onClick={novo}><Plus size={16}/> Nova oportunidade</button>}
        </div>
      </div>

      {/* Resumo por estágio */}
      <div className="grade-contadores" style={s.statusGrid}>
        {ESTAGIOS.map(e => (
          <div key={e.id} style={s.statusCard}>
            <span style={{ ...s.statusBadge, color: e.cor, borderColor: e.cor }}>{e.label}</span>
            <div style={{ ...s.statusNum, color: e.cor }}>{opps.filter(o => o.estagio === e.id).length}</div>
          </div>
        ))}
      </div>

      {/* Filtros + visão */}
      {!form && (
        <div style={s.filtros}>
          <div style={s.visaoTabs}>
            <button style={{ ...s.visaoTab, ...(visao === 'lista' ? s.visaoTabAtiva : {}) }} onClick={() => setVisao('lista')}>📋 Lista</button>
            <button style={{ ...s.visaoTab, ...(visao === 'kanban' ? s.visaoTabAtiva : {}) }} onClick={() => setVisao('kanban')}>📊 Kanban</button>
            <button style={{ ...s.visaoTab, ...(visao === 'followups' ? s.visaoTabAtiva : {}) }} onClick={() => setVisao('followups')}><AlertCircle size={13}/> Follow-ups {followupsPendentes.length > 0 && <span style={s.fuBadge}>{followupsPendentes.length}</span>}</button>
          </div>
          {visao !== 'followups' && (
            <div style={s.filtroLinha}>
              <select value={filtroEstagio} onChange={e => setFiltroEstagio(e.target.value)} style={s.filtroSel}>
                <option value="TODOS">Todos os estágios</option>
                {ESTAGIOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
              {isAdmin && (
                <select value={filtroResponsavel} onChange={e => setFiltroResponsavel(e.target.value)} style={s.filtroSel}>
                  <option value="TODOS">Todos os responsáveis</option>
                  {opcoesResponsavel.map(o => <option key={o.valor} value={o.valor}>{o.label}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {/* Formulário */}
      {form && (
        <div style={s.formCard}>
          <h3 style={s.formTitulo}>{form.id && opps.find(o => o.id === form.id) ? '✏️ Editar oportunidade' : '🎯 Nova oportunidade'}</h3>

          {/* Identificação */}
          <div style={s.secaoForm}>
            <div style={s.secaoLabel}>Identificação</div>
            <div style={s.formRow}>
              <div style={s.campo}>
                <label style={s.label}>Cliente / Lead *</label>
                {(clientes || []).length > 0
                  ? <input list="lista-clientes" type="text" style={s.input} value={form.cliente} placeholder="Nome do cliente ou lead" onChange={e => setForm({ ...form, cliente: e.target.value })} />
                  : <input type="text" style={s.input} value={form.cliente} placeholder="Nome do cliente ou lead" onChange={e => setForm({ ...form, cliente: e.target.value })} />
                }
                <datalist id="lista-clientes">
                  {(clientes || []).map(c => <option key={c.id} value={c.nome} />)}
                </datalist>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Empresa</label>
                <input type="text" style={s.input} value={form.empresa} placeholder="Nome da empresa" onChange={e => setForm({ ...form, empresa: e.target.value })} />
              </div>
            </div>
            <div style={s.formRow}>
              <div style={s.campo}>
                <label style={s.label}>Origem</label>
                <select style={s.input} value={form.origem} onChange={e => setForm({ ...form, origem: e.target.value })}>
                  {ORIGENS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Estágio</label>
                <select style={s.input} value={form.estagio} onChange={e => setForm({ ...form, estagio: e.target.value })}>
                  {ESTAGIOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                </select>
              </div>
              <div style={s.campo}>
                <label style={s.label}>Responsável</label>
                <select style={s.input} value={form.responsavel} onChange={e => setForm({ ...form, responsavel: e.target.value })}>
                  <option value="">Selecione</option>
                  {opcoesResponsavel.map(u => <option key={u.valor} value={u.valor}>{u.label}</option>)}
                </select>
              </div>
            </div>
            <div style={s.formRow}>
              <div style={s.campo}>
                <label style={s.label}>Valor estimado (R$)</label>
                <input type="number" step="0.01" style={s.input} value={form.valorEstimado} onChange={e => setForm({ ...form, valorEstimado: e.target.value })} />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Probabilidade (%)</label>
                <input type="number" min="0" max="100" style={s.input} value={form.probabilidade} onChange={e => setForm({ ...form, probabilidade: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Contatos */}
          <div style={s.secaoForm}>
            <div style={s.secaoLabel}>Contatos da empresa</div>
            {(form.contatos || []).map(c => (
              <div key={c.id} style={s.contatoEditor}>
                <input type="text" style={{ ...s.input, flex: 2 }} value={c.nome} placeholder="Nome" onChange={e => atualizarContato(c.id, 'nome', e.target.value)} />
                <input type="text" style={{ ...s.input, flex: 1 }} value={c.cargo} placeholder="Cargo" onChange={e => atualizarContato(c.id, 'cargo', e.target.value)} />
                <input type="email" style={{ ...s.input, flex: 2 }} value={c.email} placeholder="E-mail" onChange={e => atualizarContato(c.id, 'email', e.target.value)} />
                <input type="text" style={{ ...s.input, flex: 1 }} value={c.whatsapp} placeholder="WhatsApp" onChange={e => atualizarContato(c.id, 'whatsapp', e.target.value)} />
                <button style={s.btnDelLinha} onClick={() => removerContato(c.id)}><Trash2 size={14}/></button>
              </div>
            ))}
            {novoContato === null
              ? <button style={s.btnAddInline} onClick={() => setNovoContato(CONTATO_VAZIO())}><Plus size={14}/> Adicionar contato</button>
              : (
                <div style={s.contatoEditor}>
                  <input type="text" style={{ ...s.input, flex: 2 }} value={novoContato.nome} placeholder="Nome *" onChange={e => setNovoContato({ ...novoContato, nome: e.target.value })} />
                  <input type="text" style={{ ...s.input, flex: 1 }} value={novoContato.cargo} placeholder="Cargo" onChange={e => setNovoContato({ ...novoContato, cargo: e.target.value })} />
                  <input type="email" style={{ ...s.input, flex: 2 }} value={novoContato.email} placeholder="E-mail" onChange={e => setNovoContato({ ...novoContato, email: e.target.value })} />
                  <input type="text" style={{ ...s.input, flex: 1 }} value={novoContato.whatsapp} placeholder="WhatsApp" onChange={e => setNovoContato({ ...novoContato, whatsapp: e.target.value })} />
                  <button style={s.btnConfirmar} onClick={adicionarContato}>OK</button>
                  <button style={s.btnDelLinha} onClick={() => setNovoContato(null)}><X size={14}/></button>
                </div>
              )
            }
          </div>

          {/* Histórico de interações */}
          <div style={s.secaoForm}>
            <div style={s.secaoLabel}>Histórico de interações</div>
            {(form.interacoes || []).slice().sort((a,b)=>(b.data||'').localeCompare(a.data||'')).map(i => {
              const canal = CANAIS.find(c => c.id === i.canal);
              return (
                <div key={i.id} style={s.interacaoCard}>
                  <div style={s.interacaoHead}>
                    <span style={s.canalBadge}>{canal?.emoji} {canal?.label}</span>
                    <span style={{ fontSize: 12, color: '#5f5874' }}>{i.data?.replace('T', ' ')}</span>
                    {i.autor && <span style={{ fontSize: 12, color: '#8b5cf6' }}>· {i.autor}</span>}
                    <button style={s.btnDelLinha} onClick={() => removerInteracao(i.id)}><Trash2 size={12}/></button>
                  </div>
                  {i.destinatarios && <div style={{ fontSize: 12, color: '#5f5874' }}>Para: {i.destinatarios}</div>}
                  <div style={{ fontSize: 13, color: '#2d2640', whiteSpace: 'pre-line' }}>{i.observacao}</div>
                  {(i.anexos || []).length > 0 && (
                    <div style={s.anexosList}>
                      {(i.anexos || []).map((a, idx) => (
                        <button key={idx} style={s.anexoChip} onClick={() => baixarAnexo(a)}>
                          <Paperclip size={11}/> {a.nome} <Download size={11}/>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {novaInteracao === null
              ? <button style={s.btnAddInline} onClick={iniciarInteracao}><Plus size={14}/> Registrar nova interação</button>
              : (
                <div style={s.novaInteracaoCard}>
                  <div style={s.formRow}>
                    <div style={s.campo}>
                      <label style={s.label}>Canal</label>
                      <select style={s.input} value={novaInteracao.canal} onChange={e => setNovaInteracao({ ...novaInteracao, canal: e.target.value })}>
                        {CANAIS.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
                      </select>
                    </div>
                    <div style={s.campo}>
                      <label style={s.label}>Data e hora</label>
                      <input type="datetime-local" style={s.input} value={novaInteracao.data} onChange={e => setNovaInteracao({ ...novaInteracao, data: e.target.value })} />
                    </div>
                    <div style={s.campo}>
                      <label style={s.label}>Autor</label>
                      <select style={s.input} value={novaInteracao.autor} onChange={e => setNovaInteracao({ ...novaInteracao, autor: e.target.value })}>
                        <option value="">Selecione</option>
                        {opcoesResponsavel.map(u => <option key={u.valor} value={u.valor}>{u.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={s.campo}>
                    <label style={s.label}>Destinatários (separados por vírgula)</label>
                    <input type="text" style={s.input} value={novaInteracao.destinatarios} placeholder="ex.: contato@empresa.com, gerente@empresa.com" onChange={e => setNovaInteracao({ ...novaInteracao, destinatarios: e.target.value })} />
                  </div>
                  <div style={s.campo}>
                    <label style={s.label}>Descrição da interação *</label>
                    <textarea style={{ ...s.input, resize: 'vertical', minHeight: 70 }} value={novaInteracao.observacao} placeholder="O que foi discutido, próximos passos, etc." onChange={e => setNovaInteracao({ ...novaInteracao, observacao: e.target.value })} />
                  </div>
                  <div style={s.campo}>
                    <label style={s.label}>Anexos (PDF da proposta enviada, prints, etc.)</label>
                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => anexarArquivo(e.target.files)} />
                    <button style={s.btnAddInline} onClick={() => fileInputRef.current?.click()}><Paperclip size={14}/> Anexar arquivos</button>
                    {(novaInteracao.anexos || []).length > 0 && (
                      <div style={s.anexosList}>
                        {(novaInteracao.anexos || []).map((a, idx) => (
                          <span key={idx} style={s.anexoChip}>
                            <Paperclip size={11}/> {a.nome}
                            <button style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', padding: 0 }} onClick={() => setNovaInteracao(i => ({ ...i, anexos: (i.anexos || []).filter((_, j) => j !== idx) }))}><X size={12}/></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button style={s.btnSalvar} onClick={adicionarInteracao}>Registrar</button>
                    <button style={s.btnCancelar} onClick={() => setNovaInteracao(null)}>Cancelar</button>
                  </div>
                </div>
              )
            }
          </div>

          {/* Próximo contato */}
          <div style={s.secaoForm}>
            <div style={s.secaoLabel}>Próximo follow-up</div>
            <div style={s.formRow}>
              <div style={s.campo}>
                <label style={s.label}>Data do próximo contato</label>
                <input type="date" style={s.input} value={form.proximoContato} onChange={e => setForm({ ...form, proximoContato: e.target.value })} />
              </div>
              <div style={s.campo}>
                <label style={s.label}>Responsável pelo follow-up</label>
                <select style={s.input} value={form.proximoResponsavel} onChange={e => setForm({ ...form, proximoResponsavel: e.target.value })}>
                  <option value="">Selecione</option>
                  {opcoesResponsavel.map(u => <option key={u.valor} value={u.valor}>{u.label}{u.email ? ` (${u.email})` : ''}</option>)}
                </select>
              </div>
            </div>
            <div style={s.campo}>
              <label style={s.label}>Observação do follow-up</label>
              <input type="text" style={s.input} value={form.proximoObs} placeholder="O que precisa ser feito no próximo contato" onChange={e => setForm({ ...form, proximoObs: e.target.value })} />
            </div>
            {form.proximoResponsavel && emailDoResponsavel(form.proximoResponsavel) && (
              <div style={s.dicaEmail}>
                <Mail size={12}/> Lembrete será enviado para <strong>{emailDoResponsavel(form.proximoResponsavel)}</strong> no dia agendado.
              </div>
            )}
          </div>

          {/* Vincular propostas */}
          <div style={s.secaoForm}>
            <div style={s.secaoLabel}>Propostas vinculadas</div>
            {propostasVinculadas.length === 0 && orcamentosVinculados.length === 0 && <div style={{ fontSize: 12, color: '#5f5874' }}>Nenhuma proposta vinculada ainda.</div>}
            {propostasVinculadas.map(p => (
              <div key={p.id} style={s.propostaVinculadaItem}>
                <FileText size={13}/> <span style={{ flex: 1 }}>Proposta — {p.cliente} — {p.data} — {p.valor ? `R$ ${parseFloat(p.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}</span>
                <button style={s.btnDelLinha} onClick={() => desvincularProposta(p.id)} aria-label="Desvincular proposta"><X size={12}/></button>
              </div>
            ))}
            {orcamentosVinculados.map(o => (
              <div key={`o-${o.id}`} style={s.propostaVinculadaItem}>
                <FileText size={13}/> <span style={{ flex: 1 }}>Orçamento {o.numero} — {o.cliente} — {o.data} — {o.status}</span>
                <button style={s.btnDelLinha} onClick={() => desvincularOrcamento(o.id)} aria-label="Desvincular orçamento"><X size={12}/></button>
              </div>
            ))}
            {!vincularProposta
              ? <button style={s.btnAddInline} onClick={() => setVincularProposta(true)}><LinkIcon size={14}/> Vincular proposta existente</button>
              : (
                <div style={s.vincularBox}>
                  {vinculaveisDisponiveis.length === 0
                    ? <div style={{ fontSize: 12, color: '#5f5874' }}>Nenhuma proposta disponível. Crie uma em Documentos &gt; Propostas ou um orçamento em Financeiro e volte aqui.</div>
                    : vinculaveisDisponiveis.map(it => (
                        <button key={it.chave} style={s.propostaItemSelect} onClick={it.vincular}>
                          <FileText size={13}/> {it.texto}
                        </button>
                      ))
                  }
                  <button style={s.btnCancelar} onClick={() => setVincularProposta(false)}>Fechar</button>
                </div>
              )
            }
          </div>

          {/* Observações */}
          <div style={s.campo}>
            <label style={s.label}>Observações gerais</label>
            <textarea style={{ ...s.input, resize: 'vertical', minHeight: 60 }} value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} />
          </div>

          <div style={s.formActions}>
            <button style={s.btnSalvar} onClick={salvar}>Salvar</button>
            <button style={s.btnCancelar} onClick={fechar}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Conteúdo conforme visão */}
      {!form && visao === 'lista' && (
        oppsVisiveis.length === 0
          ? <div style={s.vazio}><Target size={40} color="#e5e0ef"/><p>Nenhuma oportunidade nesta visão.</p></div>
          : oppsVisiveis.map(renderCard)
      )}
      {!form && visao === 'kanban' && renderKanban()}
      {!form && visao === 'followups' && renderFollowups()}
    </div>
  );
}

const s = {
  container:    { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' },
  titulo:       { fontSize: '1.8rem', fontWeight: 700, color: '#1c1530' },
  btnNovo:      { display: 'flex', alignItems: 'center', gap: 6, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 20, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  statusGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' },
  statusCard:   { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  statusBadge:  { fontSize: 10, fontWeight: 700, border: '1.5px solid', borderRadius: 20, padding: '2px 8px', alignSelf: 'flex-start', letterSpacing: '0.05em' },
  statusNum:    { fontSize: '1.4rem', fontWeight: 700 },
  filtros:      { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  visaoTabs:    { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  visaoTab:     { display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 20, color: '#5f5874', padding: '6px 14px', fontSize: 12, cursor: 'pointer' },
  visaoTabAtiva:{ background: '#7c3aed', border: '1px solid #7c3aed', color: 'white' },
  fuBadge:      { background: '#ef4444', color: 'white', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700 },
  filtroLinha:  { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  filtroSel:    { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '6px 10px', fontSize: 12 },
  formCard:     { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  formTitulo:   { fontSize: '1.1rem', fontWeight: 700, color: '#1c1530', margin: 0 },
  secaoForm:    { background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 10, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  secaoLabel:   { fontSize: 12, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.08em' },
  formRow:      { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  campo:        { display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 140 },
  label:        { fontSize: 11, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:        { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  contatoEditor:{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' },
  btnAddInline: { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px dashed #7c3aed', borderRadius: 10, color: '#8b5cf6', cursor: 'pointer', padding: '8px 16px', fontSize: 12, fontWeight: 600, justifyContent: 'center' },
  btnConfirmar: { background: '#10b981', border: 'none', borderRadius: 6, color: 'white', cursor: 'pointer', padding: '6px 10px', fontSize: 12, fontWeight: 600 },
  btnDelLinha:  { background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4, display: 'flex' },
  interacaoCard:{ background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  interacaoHead:{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  canalBadge:   { background: 'rgba(124,58,237,0.15)', color: '#6d28d9', borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 600 },
  novaInteracaoCard: { background: '#f7f5fb', border: '1px solid #7c3aed', borderRadius: 10, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  anexosList:   { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  anexoChip:    { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '4px 10px', fontSize: 11, color: '#2d2640', cursor: 'pointer' },
  dicaEmail:    { fontSize: 12, color: '#5f5874', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(124,58,237,0.05)', borderRadius: 6, border: '1px solid rgba(124,58,237,0.2)' },
  vincularBox:  { display: 'flex', flexDirection: 'column', gap: '0.4rem', background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 8, padding: '0.75rem' },
  propostaItemSelect: { display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 6, color: '#2d2640', cursor: 'pointer', padding: '8px 12px', fontSize: 12, textAlign: 'left' },
  propostaVinculadaItem: { display: 'flex', alignItems: 'center', gap: 6, background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#2d2640' },
  formActions:  { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap', paddingTop: '0.5rem', borderTop: '1px solid #eeeaf5' },
  btnSalvar:    { background: '#7c3aed', border: 'none', borderRadius: 8, color: 'white', cursor: 'pointer', padding: '9px 22px', fontSize: 13, fontWeight: 600 },
  btnCancelar:  { background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 8, color: '#5f5874', cursor: 'pointer', padding: '9px 16px', fontSize: 13 },
  btnSec:       { display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 6, color: '#2d2640', cursor: 'pointer', padding: '5px 10px', fontSize: 12 },
  vazio:        { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '3rem', color: '#5f5874', fontSize: 14 },
  oppCard:      { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, overflow: 'hidden' },
  oppHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', cursor: 'pointer', gap: '0.5rem' },
  oppInfo:      { display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 },
  oppNome:      { fontSize: 14, fontWeight: 700, color: '#1c1530' },
  oppMeta:      { fontSize: 12, color: '#5f5874' },
  oppFu:        { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, border: '1px solid', borderRadius: 12, padding: '2px 8px', alignSelf: 'flex-start', marginTop: 4 },
  oppAcoes:     { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  estagioBadge: { fontSize: 11, fontWeight: 700, border: '1.5px solid', borderRadius: 20, padding: '2px 10px' },
  btnAcao:      { background: 'transparent', border: 'none', color: '#8b5cf6', cursor: 'pointer', padding: 4, display: 'flex' },
  btnAcaoDel:   { background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4, display: 'flex' },
  oppDetalhes:  { borderTop: '1px solid #eeeaf5', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  detLabel:     { fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  detLinha:     { fontSize: 13, color: '#2d2640' },
  contatoChip:  { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', fontSize: 12, color: '#2d2640', padding: '4px 0' },
  linkContato:  { display: 'inline-flex', alignItems: 'center', gap: 3, color: '#8b5cf6', textDecoration: 'none', fontSize: 11 },
  interacaoItem:{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem', background: '#ffffff', borderRadius: 6, padding: '6px 10px' },
  propostaChip: { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#ffffff', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: '#2d2640', marginRight: 4, marginBottom: 4 },

  // Kanban
  kanban:       { display: 'flex', gap: '0.6rem', overflowX: 'auto', paddingBottom: '0.5rem' },
  kanbanCol:    { flex: '1 0 220px', minWidth: 220, background: '#ffffff', border: '1px solid #e5e0ef', borderTopWidth: 3, borderRadius: 8, display: 'flex', flexDirection: 'column' },
  kanbanColHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0.9rem', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  kanbanCount:  { background: '#ffffff', borderRadius: 12, padding: '0 8px', fontSize: 11 },
  kanbanBody:   { padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 100 },
  kanbanCard:   { background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 6, padding: '0.6rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  kanbanCardNome:{ fontSize: 13, fontWeight: 600, color: '#1c1530' },
  kanbanCardMeta:{ fontSize: 11, color: '#5f5874' },
  kanbanCardFu: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, border: '1px solid', borderRadius: 10, padding: '1px 6px', alignSelf: 'flex-start' },
  kanbanCardActions:{ marginTop: 4 },
  kanbanSelect: { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 4, color: '#2d2640', padding: '3px 6px', fontSize: 11, width: '100%' },
  kanbanVazio:  { fontSize: 11, color: '#e5e0ef', textAlign: 'center', padding: '0.75rem' },

  // Follow-ups
  fuList:       { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  fuCard:       { background: '#ffffff', border: '1px solid #e5e0ef', borderLeft: '4px solid', borderRadius: 8, padding: '0.9rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  fuHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' },
  fuNome:       { fontSize: 14, fontWeight: 700, color: '#1c1530' },
  fuStatus:     { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, border: '1px solid', borderRadius: 12, padding: '2px 8px', marginTop: 4 },
  fuRespBox:    { textAlign: 'right' },
  fuResp:       { fontSize: 13, color: '#2d2640', fontWeight: 600 },
  fuObs:        { fontSize: 12, color: '#2d2640' },
  fuActions:    { display: 'flex', gap: '0.5rem' },
};
